import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Headset, MessageCircle, PhoneCall, RotateCcw, Send, Sparkles, X } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import CallbackForm from "./CallbackForm";
import { MessengerIcon, WhatsAppIcon, messengerLink, waLink } from "./SocialLinks";
import { recommendPackage, quizDone, quizQuestions as quizQuestionsOf } from "./PackageQuiz";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "tb-chat-session";
const DOT = { available: "bg-brand-teal", busy: "bg-brand-yellow", away: "bg-slate-400" };

export default function ChatWidget({ content }) {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat");
  const [reason, setReason] = useState("");
  const [availability, setAvailability] = useState("away");
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [hasNew, setHasNew] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    api.get("/chat/status").then((r) => setAvailability(r.data.availability)).catch(() => {});
  }, []);

  const transcript = () => {
    const lines = messages.filter((m) => m.role !== "system" && m.text).slice(-8)
      .map((m) => `${m.role === "user" ? t.chat.you : m.role === "agent" ? t.chat.agent_badge : t.chat.office_badge}: ${m.text}`);
    const body = lines.join("\n").slice(0, 1200);
    return body ? `${t.chat.transcript_intro} ${window.location.origin}\n\n${body}\n\n` : "";
  };

  const scrollDown = () => requestAnimationFrame(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }));

  const startSession = useCallback(async () => {
    try {
      const r = await api.post("/chat/sessions", { lang });
      localStorage.setItem(STORAGE_KEY, r.data.id);
      setSession(r.data.id);
      setMessages(r.data.messages);
      return r.data.id;
    } catch (err) {
      setMessages([{ id: `err-${Date.now()}`, role: "system", text: err.response?.status === 429 ? t.errors.too_many : t.chat.error }]);
      return null;
    }
  }, [lang, t]);

  const loadSession = useCallback(async () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return startSession();
    try {
      const r = await api.get(`/chat/sessions/${saved}`);
      setSession(saved);
      setMessages(r.data.messages);
      setAvailability(r.data.availability);
      return saved;
    } catch {
      return startSession();
    }
  }, [startSession]);

  const goCallback = (why) => {
    setReason(why);
    setView("callback");
  };

  // When opening the widget, decide the view based on operator availability.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      let avail = availability;
      try {
        const r = await api.get("/chat/status");
        avail = r.data.availability;
        if (!cancelled) setAvailability(avail);
      } catch {}
      if (cancelled) return;
      if (avail === "available") {
        if (!session) await loadSession();
        setView("chat");
        scrollDown();
      } else {
        goCallback(avail === "busy" ? t.chat.busy_reason : t.chat.away_reason);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Poll for new operator messages + availability while a session is active.
  useEffect(() => {
    if (!session) return undefined;
    const tick = async () => {
      try {
        const last = messages[messages.length - 1]?.created_at;
        const r = await api.get(`/chat/sessions/${session}`, { params: last ? { after: last } : {} });
        setAvailability(r.data.availability);
        if (r.data.messages.length) {
          setMessages((prev) => {
            const ids = new Set(prev.map((m) => m.id));
            return [...prev, ...r.data.messages.filter((m) => !ids.has(m.id))];
          });
          if (r.data.messages.some((m) => m.role === "agent") && !open) setHasNew(true);
          scrollDown();
        }
      } catch {}
    };
    const id = setInterval(tick, open ? 3000 : 12000);
    return () => clearInterval(id);
  }, [session, messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || !session) return;
    setInput("");
    try {
      const r = await api.post(`/chat/sessions/${session}/messages`, { text });
      setMessages((m) => [...m, r.data.message]);
      scrollDown();
    } catch (err) {
      setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "system", text: err.response?.status === 429 ? t.errors.too_many : t.chat.error }]);
    }
  };

  const quizQuestions = quizQuestionsOf(t.quiz);
  const pkgLabel = (p) => (lang === "pl" ? p.name : (t.pricing.pkgs && t.pricing.pkgs[p.id] && t.pricing.pkgs[p.id].name) || p.name);

  const startQuiz = () => {
    setQuiz({ answers: [] });
    setMessages((m) => [...m, { id: `quiz-q-${Date.now()}`, role: "assistant", text: `${t.quiz.sub}\n\n${quizQuestions[0].text}`, quizStep: 0 }]);
    scrollDown();
  };

  const answerQuiz = (opt) => {
    if (!quiz) return;
    const step = quiz.answers.length;
    const answers = [...quiz.answers, opt];
    const now = Date.now();
    const userMsg = { id: `quiz-a-${now}`, role: "user", text: quizQuestions[step][opt] };
    const finished = quizDone(answers, quizQuestions.length);
    if (finished) {
      const id = recommendPackage(answers);
      const pkg = (content.packages || []).find((p) => p.id === id);
      const name = pkg ? pkgLabel(pkg) : id;
      setQuiz(null);
      setMessages((m) => [...m, userMsg, { id: `quiz-r-${now}`, role: "assistant", text: `${t.quiz.result_overline}: ${name}.\n${t.quiz.result_reason[id]}`, orderPackage: id, orderLabel: `${t.quiz.order} ${name}` }]);
    } else {
      setQuiz({ answers });
      setMessages((m) => [...m, userMsg, { id: `quiz-q-${now}`, role: "assistant", text: quizQuestions[answers.length].text, quizStep: answers.length }]);
    }
    scrollDown();
  };

  const openOrder = (pkgId) => {
    setOpen(false);
    window.dispatchEvent(new CustomEvent("tb:open-order", { detail: pkgId }));
  };

  const endSession = useCallback((sid, beacon = false) => {
    const id = sid || session;
    localStorage.removeItem(STORAGE_KEY);
    if (!id) return;
    const url = `${API}/chat/sessions/${id}/end`;
    if (beacon && navigator.sendBeacon) navigator.sendBeacon(url);
    else fetch(url, { method: "POST", credentials: "include", keepalive: true }).catch(() => {});
  }, [session]);

  const closeChat = () => {
    if (session) endSession(session);
    setSession(null);
    setMessages([]);
    setQuiz(null);
    setView("chat");
    setOpen(false);
    setHasNew(false);
  };

  useEffect(() => {
    const handler = () => { if (session) endSession(session, true); };
    window.addEventListener("pagehide", handler);
    return () => window.removeEventListener("pagehide", handler);
  }, [session, endSession]);

  const reset = async () => {
    if (session) endSession(session);
    setQuiz(null);
    setMessages([]);
    setSession(null);
    setView("chat");
    await startSession();
  };

  const toggle = () => {
    if (open) { closeChat(); return; }
    setOpen(true);
    setHasNew(false);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-panel"
            className="fixed z-50 inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-24 sm:w-[380px] h-[min(78vh,620px)] flex flex-col rounded-2xl border border-border bg-card/95 backdrop-blur-xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-brand-navy text-white">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <Headset size={17} />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-sm truncate">{t.chat.title}</div>
                  <div data-testid="chat-status" className="flex items-center gap-1.5 text-[11px] text-slate-300">
                    <span className={`w-1.5 h-1.5 rounded-full ${DOT[availability]} ${availability === "available" ? "animate-pulse-dot" : ""}`} />
                    {t.chat.status[availability]}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {view === "chat" && session && (
                  <button data-testid="chat-reset-button" onClick={reset} aria-label={t.chat.new_chat} title={t.chat.new_chat} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                    <RotateCcw size={14} />
                  </button>
                )}
                <button data-testid="chat-close-button" onClick={toggle} aria-label={t.chat.close} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                  <X size={16} />
                </button>
              </div>
            </div>

            {view === "callback" ? (
              <CallbackForm sessionId={session} reason={reason} onBack={() => setView("chat")} />
            ) : (
              <>
                <div ref={listRef} data-testid="chat-messages" data-lenis-prevent="" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                  {messages.map((m, idx) => (
                    <Bubble
                      key={m.id}
                      m={m}
                      t={t}
                      quizActive={quiz && m.quizStep === quiz.answers.length && idx === messages.length - 1}
                      quizOptions={m.quizStep !== undefined ? quizQuestions[m.quizStep] : null}
                      onQuizAnswer={answerQuiz}
                      onOrder={openOrder}
                    />
                  ))}
                </div>

                {!quiz && (
                  <div className="px-3 pt-2">
                    <button data-testid="chat-quiz-button" type="button" onClick={startQuiz} className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-1.5 bg-brand-teal text-white hover:bg-brand-tealDark transition-colors">
                      <Sparkles size={12} /> {t.chat.quiz_cta}
                    </button>
                  </div>
                )}
                <div className="px-3 pt-2">
                  <button data-testid="chat-callback-button" type="button" onClick={() => goCallback("")} className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold border border-border rounded-full px-3 py-1.5 hover:bg-muted transition-colors">
                    <PhoneCall size={12} /> {t.chat.callback_cta}
                  </button>
                </div>
                {(content.contact_whatsapp || content.contact_messenger) && (
                  <div className="px-3 pt-2">
                    <p className="text-[10px] text-muted-foreground mb-1.5">{t.chat.continue_on}</p>
                    <div className="flex gap-2">
                      {content.contact_whatsapp && (
                        <a data-testid="chat-whatsapp-link" href={waLink(content.contact_whatsapp, transcript())} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-1.5 bg-[#25D366]/15 text-[#128C7E] dark:text-[#5EDC9A] hover:bg-[#25D366]/25 transition-colors">
                          <WhatsAppIcon size={13} /> WhatsApp
                        </a>
                      )}
                      {content.contact_messenger && (
                        <a data-testid="chat-messenger-link" href={messengerLink(content.contact_messenger, transcript())} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-1.5 bg-[#0084FF]/15 text-[#0068CC] dark:text-[#7ABAFF] hover:bg-[#0084FF]/25 transition-colors">
                          <MessengerIcon size={13} /> Messenger
                        </a>
                      )}
                    </div>
                  </div>
                )}

                <form onSubmit={send} className="p-3 flex items-center gap-2">
                  <input
                    data-testid="chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={t.chat.input_ph}
                    className="flex-1 h-11 rounded-full border border-border bg-background px-4 text-sm outline-none focus:ring-2 focus:ring-slate-500/40"
                  />
                  <button
                    data-testid="chat-send-button"
                    type="submit"
                    disabled={!input.trim()}
                    aria-label={t.chat.send}
                    className="w-11 h-11 rounded-full bg-brand-teal text-white flex items-center justify-center disabled:opacity-40 hover:bg-slate-600 dark:hover:bg-slate-200 transition-colors"
                  >
                    <Send size={15} />
                  </button>
                </form>
                <p className="px-4 pb-3 text-[10px] text-muted-foreground leading-snug">{t.chat.privacy}</p>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="chat-bubble"
        onClick={toggle}
        aria-label={t.chat.bubble}
        className={`group fixed right-4 bottom-4 sm:right-6 sm:bottom-6 z-50 inline-flex items-center gap-3 bg-brand-navy text-white rounded-full shadow-xl pl-3.5 pr-5 py-3 hover:scale-[1.03] active:scale-95 transition-transform duration-200 ${open ? "sm:flex hidden" : ""}`}
      >
        <span className="relative flex w-9 h-9 items-center justify-center rounded-full bg-brand-teal">
          {!open && <span className="absolute inset-0 rounded-full bg-brand-teal/60 animate-ping" />}
          {open ? <X size={17} className="relative" /> : <MessageCircle size={17} className="relative" />}
          {hasNew && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-rose-500 ring-2 ring-slate-900 dark:ring-white" />}
        </span>
        <span className="text-left leading-none">
          <span className="block text-sm font-semibold">{t.chat.bubble}</span>
          <span className="hidden sm:block font-mono text-[10px] tracking-wide opacity-70 mt-1">{t.chat.bubble_hint}</span>
        </span>
        <span className={`w-2 h-2 rounded-full ${DOT[availability]}`} />
      </button>
    </>
  );
}

const URL_RE = /(https?:\/\/[^\s<>()]+[^\s<>().,;:!?])/g;

function linkify(text) {
  return String(text).split(URL_RE).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} data-testid="chat-message-link" href={part} target={part.startsWith(window.location.origin) ? "_self" : "_blank"} rel="noopener noreferrer" className="underline underline-offset-2 font-semibold hover:text-brand-teal">{part}</a>
    ) : part
  );
}

function Bubble({ m, t, quizActive, quizOptions, onQuizAnswer, onOrder }) {
  if (m.role === "system")
    return <div className="text-[11px] text-center text-muted-foreground italic">{m.text}</div>;
  const mine = m.role === "user";
  return (
    <div data-testid={`chat-message-${m.role}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] ${mine ? "items-end" : "items-start"} flex flex-col gap-1`}>
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-1">
          {mine ? t.chat.you : m.role === "agent" ? t.chat.agent_badge : t.chat.office_badge}
        </span>
        <div
          className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
            mine ? "bg-brand-navy text-white rounded-br-md" : "bg-muted text-foreground rounded-bl-md"
          }`}
        >
          {mine ? m.text : linkify(m.text)}
        </div>
        {quizActive && quizOptions && (
          <div data-testid="chat-quiz-options" className="flex flex-col gap-1.5 mt-1 w-full">
            {["a", "b"].map((opt) => (
              <button
                key={opt}
                data-testid={`chat-quiz-answer-${opt}`}
                onClick={() => onQuizAnswer(opt)}
                className="text-left text-xs font-medium rounded-xl border border-border bg-background px-3 py-2 hover:border-slate-500 hover:bg-muted transition-colors"
              >
                {quizOptions[opt]}
              </button>
            ))}
          </div>
        )}
        {m.orderPackage && (
          <button
            data-testid="chat-quiz-order-button"
            onClick={() => onOrder(m.orderPackage)}
            className="mt-1 self-start inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 bg-brand-teal text-white hover:bg-brand-tealDark transition-colors"
          >
            {m.orderLabel}
          </button>
        )}
      </div>
    </div>
  );
}
